import React, { useEffect, useState } from 'react'
import { API_BASE_URL, LoadingSpinnerMeta } from '../data'
import { AssignTrainingFormModalMetaProps, Training } from '../data/types'
import { DeleteIcon, PlusIcon } from '../icons'
import { LoadingSpinner } from './LoadingSpinner'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'

type Props = {
    meta: AssignTrainingFormModalMetaProps
    volunteer_trainings: Training[]
    isOpen: boolean
    onSubmit: (assignedTraining: Training[]) => Promise<void>
    onClose: () => void
}

const subtractTrainings = (setA: Training[], setB: Training[]) => {
    const bIds = new Set(setB.map(t => t.id))
    return setA.filter(t => !bIds.has(t.id))
}


export const AssignTrainingFormModal = (props: Props) => {
    const { meta, volunteer_trainings, isOpen, onClose, onSubmit } = props

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [fetchError, setFetchError] = useState<string | null>(null)
    const [trainings, setTrainings] = useState<Training[]>([])
    const [assignedTrainings, setAssignedTrainings] = useState<Training[]>([])
    const [loadingTrainings, setLoadingTrainings] = useState(false);
    const [formData, setFormData] = useState<{ training_id: string }>({
        training_id: ''
    })

    // Fetch All Trainings
    useEffect(() => {
        if (!isOpen) return;

        const loadTrainings = async () => {
            setLoadingTrainings(true);
            setError(null);
            setFetchError(null)

            try {
                const response = await fetch(`${API_BASE_URL}/training`, { credentials: 'include' });
                if (!response.ok) {
                    throw new Error("Failed to load trainings");
                }

                const result = await response.json();
                const allTrainings: Training[] = result.value || result;
                const filtered = subtractTrainings(allTrainings, volunteer_trainings);

                setTrainings(filtered);
                setAssignedTrainings([]);
                setFormData({ training_id: "" });

            } catch (err) {
                setError(err instanceof Error ? err.message : "Unable to fetch trainings");
                setFetchError("Unable to fetch trainings")
            } finally {
                setLoadingTrainings(false);
            }
        };

        loadTrainings();
    }, [isOpen, volunteer_trainings]);



    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setFormData({ training_id: e.target.value })
    }

    const handleAddTraining = () => {
        if (!formData.training_id) {
            setError(meta.noTrainingSelectedMessage)
            return
        }

        const id = Number(formData.training_id)

        const selectedTraining = trainings.find(t => t.id === id)
        if (!selectedTraining) return

        setAssignedTrainings(prev => [...prev, selectedTraining])
        setTrainings(prev => prev.filter(t => t.id !== id))

        setFormData({ training_id: '' })   // reset safely
        setError(null)
        setFetchError(null)
    }


    const handleRemoveTraining = (trainingId: number) => {
        const removed = assignedTrainings.find(t => t.id === trainingId)
        if (!removed) return

        setAssignedTrainings(prev => prev.filter(t => t.id !== removed.id))

        setTrainings(prev => [...prev, removed])
    }


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)

        try {
            if (assignedTrainings.length === 0) {
                throw new Error(meta.noTrainingSelectedMessage)
            }
            await onSubmit(assignedTrainings)
            handleClose()
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred')
        }
        finally {
            setLoading(false)
        }
    }

    const handleClose = () => {
        setFormData({ training_id: null })
        setAssignedTrainings([])
        setError(null)
        setFetchError(null)
        onClose()
    }



    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={meta.title}
            maxWidth="2xl"
            footer={
                <div className="flex justify-end gap-3">
                    <Button
                        type="button"
                        onClick={handleClose}
                        disabled={loading}
                        variant="outline"
                        color="neutral"
                    >
                        {meta.cancelButton}
                    </Button>
                    <Button
                        type="submit"
                        form="training-form"
                        disabled={loading}
                    >
                        {loading ? meta.submittingButton : meta.submitButton}
                    </Button>
                </div>
            }
        >
            <form id="training-form" onSubmit={handleSubmit}>
                <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">

                    {/* Error while loading trainings */}
                    {error && loadingTrainings === false && (
                        <div className="mb-4 bg-danger-100 dark:bg-danger-900 border border-danger-400
                        dark:border-danger-700 text-danger-700 dark:text-danger-200
                        px-4 py-3 rounded-lg">
                            <p>{error}</p>
                        </div>
                    )}

                    {/* Still loading trainings → show spinner */}
                    {loadingTrainings && (
                        <div className="flex justify-center py-8">
                            <LoadingSpinner meta={LoadingSpinnerMeta}></LoadingSpinner>
                        </div>
                    )}

                    {/* Only show form if trainings loaded and no error */}
                    {!loadingTrainings && !fetchError && (
                        <>
                            {/* Training Selection */}
                            <div className="mb-4">
                                <label
                                    htmlFor="training_id"
                                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                                >
                                    {meta.trainingLabel} <span className="text-danger-500">*</span>
                                </label>

                                <div className="flex items-center gap-3 mb-4">
                                    <select
                                        id="training_id"
                                        name="training_id"
                                        value={formData.training_id}
                                        onChange={handleChange}
                                        className="flex-grow px-3 py-2 border border-gray-300 dark:border-gray-600
                                   rounded-md dark:bg-gray-700 dark:text-gray-100"
                                    >
                                        <option value="">{meta.trainingPlaceHolder}</option>
                                        {trainings.map((t) => (
                                            <option key={t.id} value={t.id}>{t.name}</option>
                                        ))}
                                    </select>

                                    <Button
                                        type="button"
                                        onClick={handleAddTraining}
                                        variant="outline"
                                        size="sm"
                                        className="h-full flex items-center gap-1"
                                    >
                                        <PlusIcon className="w-4 h-4" />
                                        {meta.addTrainingButton}
                                    </Button>
                                </div>

                                {/* Assigned Trainings */}
                                {assignedTrainings.length === 0 ? (
                                    <div className="text-center py-8 border-2 border-dashed border-gray-300
                                    dark:border-gray-600 rounded-lg">
                                        <p className="text-gray-500 dark:text-gray-400">
                                            {meta.noTrainingMessage}
                                        </p>
                                    </div>
                                ) : (
                                    assignedTrainings.map((t) => (
                                        <div
                                            key={t.id}
                                            className="border dark:text-white border-gray-300 dark:border-gray-600
                                       rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50 mb-3"
                                        >
                                            <div className="flex justify-between">
                                                {t.name}
                                                <Button color="danger" onClick={() => handleRemoveTraining(t.id)}>
                                                    <DeleteIcon />
                                                </Button>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>
            </form>
        </Modal >
    )

}
